import React, { useState, useEffect } from 'react';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import Label from 'components/v2/forms/Label';
import Select from 'components/v2/select';
import TextField from 'components/v2/forms/TextField';
import Switch from 'react-switch';
import EditableSection from 'components/v2/EditableSection';
import { useTranslation } from 'react-i18next';
import SessionStore from 'stores/SessionStore';
import {
  checkPermissonForEditShipperBillingModule,
  payloadMappingOnlyForChanges,
} from './utils';

export const AccountingSummary = ({ fields }) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-4">
      <DescriptionListItem label={t('ACCOUNTING_HOLD')} className="mb-4">
        {fields.reason || t('NOT_SET')}
      </DescriptionListItem>
      <DescriptionListItem label={t('NETSUITE_ID')} className="mb-4">
        {fields.net_suite_id}
      </DescriptionListItem>
      <DescriptionListItem label={t('CAN_PAY_MANUALLY')} className="mb-4">
        {fields.can_pay_manually ? t('YES') : t('NO')}
      </DescriptionListItem>
      <DescriptionListItem label={t('CRM_MAPPING_ID')} className="mb-4">
        {fields.crm_mapping_id}
      </DescriptionListItem>
    </div>
  );
};

export const AccountingForm = ({
  onChange,
  fields,
  saving,
  onSaveShipper,
  onSaveSuccess,
  onSaveFail,
}) => {
  const { t } = useTranslation();
  const options = [
    {
      slug: 'good_standing',
      name: 'Good Standing',
      localizationSlug: 'GOOD_STANDING',
    },
    {
      slug: 'on_hold',
      name: 'On Hold',
      localizationSlug: 'ON_HOLD',
    },
  ];

  const saveForm = async () => {
    try {
      await onSaveShipper();
      onSaveSuccess(fields);
    } catch (e) {
      onSaveFail();
    }
  };

  useEffect(() => {
    if (saving) {
      saveForm();
    }
  }, [saving]);

  const accountingAdminAccess = () => {
    return SessionStore.hasAccountingAdminRole() || SessionStore.isAdmin();
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      {accountingAdminAccess() && (
        <div className="">
          <Label htmlFor="accountingHold">{t('ACCOUNTING_HOLD')}</Label>
          <Select
            id="accountingHold"
            labelClassName="m-0"
            items={options}
            selected={fields.reason}
            onSelect={({ name }) => onChange('reason', name)}
            valueKey="name"
            textKey="localizationSlug"
          />
        </div>
      )}
      <div>
        <Label htmlFor="netsuiteId">{t('NETSUITE_ID')}</Label>
        <TextField
          className="mt-1"
          value={fields.net_suite_id}
          onChange={(val) => onChange('net_suite_id', val)}
        />
      </div>
      <div>
        <Label htmlFor="can_pay_manually">{t('CAN_PAY_MANUALLY')}</Label>
        <Switch
          id="can_pay_manually"
          className="mt-1"
          checked={fields.can_pay_manually}
          onChange={(val) => onChange('can_pay_manually', val)}
        />
      </div>
      <div>
        <Label htmlFor="crm_mapping_id">{t('CRM_MAPPING_ID')}</Label>
        <TextField
          value={fields.crm_mapping_id}
          className="mt-1"
          onChange={(val) => onChange('crm_mapping_id', val)}
        />
      </div>
    </div>
  );
};

const AccountingSettings = ({ className, onUpdateShipper, data }) => {
  const { t } = useTranslation();
  const [initialData, setinitialData] = useState(data);
  const [accountingData, setAccountingData] = useState(data);

  const getInitData = () => {
    if (data) {
      setinitialData(data);
      setAccountingData(data);
    }
  };
  useEffect(() => {
    getInitData();
  }, [data]);

  const handleSave = async () => {
    const payload = payloadMappingOnlyForChanges(initialData, accountingData);
    if (payload.reason) {
      payload.accounting = {
        on_hold: payload.reason !== 'Good Standing',
        reason: payload.reason,
      };
    }
    if (Object.keys(payload).length) {
      await onUpdateShipper(payload);
    }
  };
  const onChange = (field, value) => {
    setAccountingData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAfterSave = (onSaveSuccess) => {
    return (updatedData = {}) => {
      setinitialData((prev) => ({ ...prev, ...updatedData }));
      onSaveSuccess();
    };
  };
  return (
    <EditableSection
      className={className}
      title={t('ACCOUNTING')}
      canEdit={checkPermissonForEditShipperBillingModule()}
      onSave={handleSave}
    >
      {(editMode, saving, onSaveSuccess, onSaveFail) =>
        editMode ? (
          <AccountingForm
            fields={accountingData}
            saving={saving}
            onChange={onChange}
            onSaveSuccess={handleAfterSave(onSaveSuccess)}
            onSaveFail={() => onSaveFail()}
            onSaveShipper={() => handleSave()}
          />
        ) : (
          <AccountingSummary fields={initialData} />
        )
      }
    </EditableSection>
  );
};

export default AccountingSettings;
